import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { apiFetchPayloadBlob } from "@/api/client";
import type { ScanGeometryRef } from "@/api/scan-geometry";

/**
 * FFPOpt's wavefront scan plot, as a `blob:` URL an `<img>` can show.
 *
 * The plot is a PNG payload on the scan's `XTBScanProfile`, named by the
 * `scan-geometry` view's `wavefront_plot` (an address, like each point's
 * geometry: its fetch may 404). The payload route serves it as an attachment
 * on the API origin, so it is read with credentials into a `Blob` and shown
 * through an object URL, never by pointing `<img src>` at the route.
 */

/** The render cap. FFPOpt's figure is about 19 KB; this is far past any. */
export const WAVEFRONT_PLOT_MAX_BYTES = 8 * 1024 * 1024;

export function wavefrontPlotQueryKey(digest: string | null | undefined): readonly unknown[] {
  return ["wavefront-plot", digest] as const;
}

export interface WavefrontPlot {
  /** The object URL, or `null` while there is none to show. */
  src: string | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Fetch `plot` once per digest and own one object URL for it.
 *
 * - **The cache holds the `Blob`, never a URL.** An object URL belongs to the
 *   component that made it: one cached in the query would be revoked by the
 *   first surface to unmount while another still shows it, or by none at all.
 *   Here each mounted component makes its own URL from the shared `Blob` and
 *   revokes it on unmount and on a digest change.
 * - **Keyed on the digest, never on `download_url`.** The digest names the
 *   bytes; the URL is a location. `staleTime: Infinity` and no window-focus
 *   refetch, because bytes with one digest never change: a remount within
 *   `gcTime`, or a re-render with a new ref of the same digest (a
 *   `scan-geometry` poll), fetches nothing and makes or revokes no URL.
 * - **`retry: false`**: the dominant failure is a 404, a fact about the run
 *   (the payload never registered), not a transient fault.
 */
export function useWavefrontPlot(plot: ScanGeometryRef | null): WavefrontPlot {
  const digest = plot?.digest;
  const query = useQuery({
    queryKey: wavefrontPlotQueryKey(digest),
    queryFn: ({ signal }) => {
      if (!plot) throw new Error("useWavefrontPlot: no plot to fetch");
      return apiFetchPayloadBlob(plot.download_url, {
        maxBytes: WAVEFRONT_PLOT_MAX_BYTES,
        signal,
      });
    },
    enabled: !!digest,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const blob = digest ? query.data : undefined;

  // The URL is held WITH the blob it was made from, so a render after a digest
  // change, before this effect has run, never hands out the old URL.
  const [made, setMade] = React.useState<{ blob: Blob; url: string } | null>(null);
  React.useEffect(() => {
    if (blob === undefined) {
      setMade(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setMade({ blob, url });
    return () => URL.revokeObjectURL(url);
  }, [digest, blob]);

  const src = blob !== undefined && made?.blob === blob ? made.url : null;
  return {
    src,
    // Still loading for the one render between the data landing and its URL.
    loading: !!digest && (query.isPending || (blob !== undefined && src === null)),
    error: digest && query.isError ? query.error : null,
  };
}
